import React from 'react';
import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import { Modal } from './Modal';
import { Button, ButtonVariant } from './Button';

const Content = styled.div`
  display: flex;
  gap: 0.9rem;
  align-items: flex-start;
`;

const IconBox = styled.div<{ $danger?: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${(p) => p.theme.radius};
  color: ${(p) => (p.$danger ? p.theme.error : p.theme.primary)};
  background: ${(p) => (p.$danger ? p.theme.error : p.theme.primary)}1a;
`;

const Message = styled.div`
  font-size: 0.92rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

export interface ConfirmModalProps {
  isOpen: boolean;
  /** Modal heading. */
  title?: string;
  /** Body message (string or nodes). */
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When true, uses the destructive (red) confirm button + icon tone. */
  danger?: boolean;
  /** Shows a spinner on the confirm button and blocks re-clicks. */
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Design-system confirmation dialog - replaces the native `window.confirm`.
 * Built on top of [Modal]; use for destructive/irreversible actions.
 */
export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title = 'Confirmar ação',
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  danger = false,
  loading = false,
  onConfirm,
  onClose,
}) => {
  const confirmVariant: ButtonVariant = danger ? 'danger' : 'primary';
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      width="440px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={confirmVariant} onClick={onConfirm} isLoading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Content>
        <IconBox $danger={danger}>
          <AlertTriangle size={20} />
        </IconBox>
        <Message>{message}</Message>
      </Content>
    </Modal>
  );
};

export default ConfirmModal;
